import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Play, Pause, SkipBack, SkipForward, Volume1, VolumeX, Volume2, FileText,
  PhoneIncoming, PhoneOutgoing, Calendar, Clock, Download, Loader2, RefreshCw,
  Upload, AlertTriangle, Info, CheckCircle2, XCircle, User, FileAudio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDuration } from "@/utils/helpers";
import {
  downloadRecordingViaPanel,
  downloadRecordingFromUrl,
  fetchRecordingBlobObjectUrl,
  isRecordingNotFoundMessage,
} from "@/utils/recordingDownload";
import {
  isPublicRecordingUrl,
  normalizePublicRecordingUrl,
  parsePublicSesCdrId,
} from "@/utils/publicRecordingUrl";
import {
  formatCallLogDateTime,
  formatPendingRemaining,
  getRecordingPendingRemainingMs,
  isRecordWithinRecordingPendingWindow,
  RECORDING_RETRY_MS,
  shouldExpectRecording,
} from "@/utils/recordingPlayerPending";

export const RECORDING_ERROR_LABELS = {
  missing: "Dosya bulunamadı / yok",
  corrupted: "Ses dosyası bozuk",
  wrong_file: "Yanlış ses dosyası",
};

const RECORDING_ERROR_HINTS = {
  missing: "Ses dosyası hiç yüklenmiyor veya bulunamıyor.",
  corrupted: "Dosya açılıyor ama ses bozuk, cızırtılı veya takılıyor.",
  wrong_file: "Dosya açılıyor ama bu çağrıya ait değil veya farklı konuşma var.",
};

const getRecordingErrorLabel = (type) => RECORDING_ERROR_LABELS[type] || type;

const getReportStatusLabel = (status) => {
  if (status === "fixed") return "Ses dosyası düzeltildi";
  if (status === "not_found") return "Ses dosyası bulunamadı";
  return "Ses dosyası inceleniyor";
};

const hasTranscriptContent = (data) => {
  if (!data) return false;
  if (data.transcript_text) return true;
  if (data.transkript === 1) return true;
  const status = (data.status || "").toLowerCase();
  return ["completed", "processing", "pending", "queued", "in_progress"].includes(status);
};

const CallRecordingPlayerModal = ({
  isOpen,
  onClose,
  audioUrl,
  recordInfo,
  isDark,
  onOpenTranscript,
  apiBase = "/api",
}) => {
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState("");
  const [panelFetching, setPanelFetching] = useState(false);
  const [panelProgress, setPanelProgress] = useState(null);
  const [panelLoadedBytes, setPanelLoadedBytes] = useState(0);
  const [panelTotalBytes, setPanelTotalBytes] = useState(null);
  const panelBlobRef = useRef("");
  const sourceRecordingUrl = audioUrl;
  const parsedSesCdrId = parsePublicSesCdrId(sourceRecordingUrl);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [transferPending, setTransferPending] = useState(false);
  const [pendingRemainingMs, setPendingRemainingMs] = useState(0);
  const [reportingType, setReportingType] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [checkingReport, setCheckingReport] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [transcriptHasContent, setTranscriptHasContent] = useState(false);
  const [transcriptChecking, setTranscriptChecking] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const audioRef = useRef(null);
  const retryTimerRef = useRef(null);
  const recordId = recordInfo?.id ?? recordInfo?.cdr_id ?? parsedSesCdrId ?? null;
  const normalizedSourceUrl = normalizePublicRecordingUrl(sourceRecordingUrl, apiBase) || sourceRecordingUrl;
  /** /api/ses/ akışı Range desteklemez → ileri sarma için tam dosya (blob) gerekir */
  const isPublicSesLink = Boolean(parsedSesCdrId)
    || isPublicRecordingUrl(sourceRecordingUrl)
    || isPublicRecordingUrl(normalizedSourceUrl);
  const usePanelBlob = Boolean(
    apiBase
    && sourceRecordingUrl
    && ((isPublicSesLink && recordId != null) || (!isPublicSesLink && recordId == null)),
  );
  const displayInfo = useMemo(() => {
    if (!recordInfo) return null;
    return {
      arayan_numara: recordInfo.arayan_numara ?? recordInfo.phone_number ?? "-",
      aranan_numara: recordInfo.aranan_numara ?? "-",
      tarih: recordInfo.tarih ?? formatCallLogDateTime(recordInfo.call_date),
      konusma_suresi: recordInfo.konusma_suresi ?? recordInfo.duration ?? 0,
    };
  }, [recordInfo]);
  const reportStatus = existingReport?.status || (existingReport ? "pending" : null);
  const withinPendingWindow = useMemo(
    () => isRecordWithinRecordingPendingWindow(recordInfo),
    [recordInfo]
  );
  const playableReportOptions = useMemo(
    () => (loadError && !transferPending ? ["missing"] : (!isLoading && !transferPending ? ["corrupted", "wrong_file"] : [])),
    [loadError, isLoading, transferPending]
  );

  const waveformBars = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  const effectiveAudioUrl = usePanelBlob ? resolvedAudioUrl : normalizedSourceUrl;
  const playerBusy = panelFetching || (usePanelBlob && !resolvedAudioUrl) || isLoading;

  const formatDlBytes = (bytes) => {
    const n = Number(bytes) || 0;
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const panelProgressLabel = useMemo(() => {
    if (!usePanelBlob || resolvedAudioUrl) return "";
    if (panelProgress != null && panelProgress >= 0) {
      const pct = Math.round(panelProgress * 100);
      const loaded = formatDlBytes(panelLoadedBytes);
      const total = panelTotalBytes ? formatDlBytes(panelTotalBytes) : null;
      return total ? `Ses indiriliyor · %${pct} · ${loaded} / ${total}` : `Ses indiriliyor · %${pct} · ${loaded}`;
    }
    if (panelLoadedBytes > 0) return `Ses indiriliyor · ${formatDlBytes(panelLoadedBytes)}`;
    return "Ses indiriliyor…";
  }, [usePanelBlob, resolvedAudioUrl, panelProgress, panelLoadedBytes, panelTotalBytes]);

  const infoRows = useMemo(() => {
    if (!recordInfo) return [];
    const rows = [];
    const push = (label, value, mono = false) => {
      if (value == null || value === "" || value === "-") return;
      rows.push({ label, value, mono });
    };
    push("Kayıt", recordInfo.title || recordInfo.context_title);
    push("Asistan", recordInfo.agent_name);
    push("Arayan", recordInfo.arayan_numara ?? recordInfo.phone_number, true);
    push("Aranan", recordInfo.aranan_numara, true);
    push("Tarih", recordInfo.tarih ?? formatCallLogDateTime(recordInfo.call_date));
    const dur = recordInfo.konusma_suresi ?? recordInfo.duration;
    if (dur != null && Number(dur) > 0) push("Süre", formatDuration(dur));
    if (recordInfo.report_row_id != null) push("Rapor kaydı", `#${recordInfo.report_row_id}`, true);
    if (recordInfo.recording_ref) push("Ses referansı", recordInfo.recording_ref, true);
    push("Kaynak", recordInfo.source_label);
    return rows;
  }, [recordInfo]);

  const resetPlayerState = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoading(true);
    setLoadError(false);
    setTransferPending(false);
    setPendingRemainingMs(0);
    setReportingType(null);
    setExistingReport(null);
    setCheckingReport(false);
    setReportMenuOpen(false);
    setTranscriptHasContent(false);
    setTranscriptChecking(false);
    if (retryTimerRef.current) {
      clearInterval(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const reloadAudio = useCallback(() => {
    if (!audioRef.current || !effectiveAudioUrl) return;
    setIsLoading(true);
    setLoadError(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    audioRef.current.load();
  }, [effectiveAudioUrl]);

  const markRecordingLoadFailure = useCallback(() => {
    setIsLoading(false);
    setIsPlaying(false);
    setDuration(0);
    setCurrentTime(0);
    if (isRecordWithinRecordingPendingWindow(recordInfo)) {
      setTransferPending(true);
      setLoadError(false);
      setPendingRemainingMs(getRecordingPendingRemainingMs(recordInfo));
    } else {
      setTransferPending(false);
      setLoadError(true);
    }
  }, [recordInfo]);

  useEffect(() => {
    if (!isOpen) {
      if (panelBlobRef.current) {
        URL.revokeObjectURL(panelBlobRef.current);
        panelBlobRef.current = "";
      }
      setResolvedAudioUrl("");
      setPanelFetching(false);
      resetPlayerState();
      return;
    }
    if (!usePanelBlob) return undefined;
    let cancelled = false;
    setPanelFetching(true);
    setPanelProgress(null);
    setPanelLoadedBytes(0);
    setPanelTotalBytes(null);
    setResolvedAudioUrl("");
    setLoadError(false);
    fetchRecordingBlobObjectUrl({
      apiBase,
      cdrId: isPublicSesLink && recordId ? recordId : null,
      recordingUrl: isPublicSesLink && recordId ? null : sourceRecordingUrl,
      onDownloadProgress: (ratio, loaded, total) => {
        if (cancelled) return;
        setPanelProgress(ratio);
        setPanelLoadedBytes(loaded || 0);
        setPanelTotalBytes(total);
      },
    })
      .then((objectUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        panelBlobRef.current = objectUrl;
        setResolvedAudioUrl(objectUrl);
        setPanelFetching(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setPanelFetching(false);
          setLoadError(true);
          const msg = err.message || "Ses kaydı yüklenemedi";
          if (isRecordingNotFoundMessage(msg)) toast.warning(msg);
          else toast.error(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, usePanelBlob, apiBase, sourceRecordingUrl, recordId, isPublicSesLink, resetPlayerState]);

  useEffect(() => {
    if (!isOpen) {
      resetPlayerState();
      return;
    }
    if (usePanelBlob && !resolvedAudioUrl) return;
    resetPlayerState();
    if (audioRef.current && effectiveAudioUrl) {
      audioRef.current.load();
    }
  }, [isOpen, effectiveAudioUrl, recordId, resetPlayerState, usePanelBlob, resolvedAudioUrl]);

  useEffect(() => {
    if (!isOpen || !transferPending || !effectiveAudioUrl) {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return undefined;
    }

    const tick = () => {
      const remaining = getRecordingPendingRemainingMs(recordInfo);
      setPendingRemainingMs(remaining);
      if (remaining <= 0) {
        setTransferPending(false);
        setLoadError(true);
        if (retryTimerRef.current) {
          clearInterval(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        return;
      }
      reloadAudio();
    };

    tick();
    retryTimerRef.current = setInterval(tick, RECORDING_RETRY_MS);
    setPendingRemainingMs(getRecordingPendingRemainingMs(recordInfo));

    return () => {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [isOpen, transferPending, effectiveAudioUrl, recordInfo, reloadAudio]);

  useEffect(() => {
    if (!isOpen || !recordId) {
      setExistingReport(null);
      setCheckingReport(false);
      return;
    }

    let cancelled = false;
    setCheckingReport(true);
    axios.get(`${apiBase}/recording-error-reports`, { params: { cdr_id: recordId } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.reported) {
          setExistingReport({
            error_type: res.data.error_type,
            status: res.data.status || "pending",
            created_at: res.data.created_at,
            reported_by_name: res.data.reported_by_name,
            resolved_at: res.data.resolved_at,
            resolved_by_name: res.data.resolved_by_name,
            resolution_note: res.data.resolution_note,
          });
        } else {
          setExistingReport(null);
        }
      })
      .catch(() => {
        if (!cancelled) setExistingReport(null);
      })
      .finally(() => {
        if (!cancelled) setCheckingReport(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, recordId, apiBase]);

  useEffect(() => {
    if (!isOpen || !recordId) {
      setTranscriptHasContent(false);
      setTranscriptChecking(false);
      return;
    }

    if (hasTranscriptContent(recordInfo)) {
      setTranscriptHasContent(true);
      setTranscriptChecking(false);
      return;
    }

    let cancelled = false;
    setTranscriptChecking(true);
    axios.get(`${apiBase}/transcripts/${recordId}`)
      .then((res) => {
        if (cancelled) return;
        setTranscriptHasContent(hasTranscriptContent(res.data));
      })
      .catch(() => {
        if (!cancelled) setTranscriptHasContent(false);
      })
      .finally(() => {
        if (!cancelled) setTranscriptChecking(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, recordId, recordInfo, apiBase]);

  const handleClose = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    resetPlayerState();
    onClose();
  }, [onClose, resetPlayerState]);

  const submitRecordingError = async (errorType) => {
    const cdrId = recordInfo?.id ?? recordInfo?.cdr_id;
    if (!cdrId || existingReport || reportingType) return;
    setReportingType(errorType);
    try {
      await axios.post(`${apiBase}/recording-error-reports`, {
        cdr_id: cdrId,
        recording_url: audioUrl,
        error_type: errorType,
        arayan_numara: recordInfo.arayan_numara,
        aranan_numara: recordInfo.aranan_numara,
        call_tarih: recordInfo.tarih,
        tarih: recordInfo.tarih,
        agent_id: recordInfo.agentId || recordInfo.agent_id,
        agent_name: recordInfo.agent_name,
        company_id: recordInfo.company_id,
      });
      setExistingReport({ error_type: errorType, status: "pending", created_at: new Date().toISOString() });
      setReportMenuOpen(false);
      toast.success("Hata bildirimi kaydedildi — ses dosyası inceleniyor");
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.already_reported) {
        setExistingReport({
          error_type: error.response.data.error_type,
          status: error.response.data.status || "pending",
          created_at: error.response.data.created_at,
        });
        toast.info(getReportStatusLabel(error.response.data.status || "pending"));
      } else {
        toast.error(error.response?.data?.detail || "Bildirim gönderilemedi");
      }
    } finally {
      setReportingType(null);
    }
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(() => {
          markRecordingLoadFailure();
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
      setIsLoading(false);
      setLoadError(false);
      setTransferPending(false);
      setPendingRemainingMs(0);
    }
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    setLoadError(false);
    setTransferPending(false);
    setPendingRemainingMs(0);
  };

  const handleAudioError = () => {
    markRecordingLoadFailure();
  };

  const mediaDuration = () => {
    const fromEl = audioRef.current?.duration;
    if (fromEl && Number.isFinite(fromEl) && fromEl > 0) return fromEl;
    return duration > 0 ? duration : 0;
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const d = mediaDuration();
    if (audioRef.current && d > 0) {
      const next = percent * d;
      audioRef.current.currentTime = next;
      setCurrentTime(next);
    }
  };

  const skip = (seconds) => {
    if (!audioRef.current) return;
    const d = mediaDuration();
    const maxT = d > 0 ? d : audioRef.current.currentTime + Math.abs(seconds);
    audioRef.current.currentTime = Math.max(0, Math.min(maxT, audioRef.current.currentTime + seconds));
  };

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  };

  const handleDownloadRecording = async () => {
    if (downloading) return;
    const info = recordInfo || {};

    setDownloading(true);
    try {
      if (usePanelBlob && sourceRecordingUrl) {
        await downloadRecordingFromUrl({
          apiBase,
          recordingUrl: sourceRecordingUrl,
          filename: info.download_filename || "recording.wav",
        });
      } else {
        const downloadSource = info.download_source || "cdr";
        const cdrId = info.cdr_id ?? recordId;
        const canDownload = downloadSource === "cdr"
          ? !!cdrId
          : !!(info.device_id && (info.recording_id ?? info.id));

        if (!canDownload) {
          toast.error("Bu kayıt için indirme referansı bulunamadı");
          return;
        }

        await downloadRecordingViaPanel({
          apiBase,
          cdrId: downloadSource === "cdr" ? cdrId : null,
          source: downloadSource,
          deviceId: info.device_id ?? null,
          recordingId: info.recording_id ?? info.id ?? null,
        });
      }
      toast.success("Ses kaydı indirildi");
    } catch (err) {
      const msg = err.message || "Ses kaydı indirilemedi";
      if (isRecordingNotFoundMessage(msg)) toast.warning(msg);
      else toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className={`max-w-2xl ${isDark ? "bg-slate-900 border-slate-800 text-white" : "bg-white"}`}>
        <DialogHeader className="space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2 pr-8">
            <DialogTitle className={`flex flex-wrap items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
              <span className="inline-flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-blue-500 shrink-0" />
                Ses Kaydı Oynatıcı
              </span>
              {recordId != null && (
                <Badge variant="outline" className={`font-mono text-xs ${isDark ? "border-cyan-500/40 text-cyan-300 bg-cyan-500/10" : "border-cyan-200 text-cyan-700 bg-cyan-50"}`}>
                  Kayıt ID: #{recordId}
                </Badge>
              )}
              {recordId == null && recordInfo?.report_row_id != null && (
                <Badge variant="outline" className={`font-mono text-xs ${isDark ? "border-violet-500/40 text-violet-300 bg-violet-500/10" : "border-violet-200 text-violet-800 bg-violet-50"}`}>
                  Rapor kaydı #{recordInfo.report_row_id}
                </Badge>
              )}
              {reportStatus === "fixed" && (
                <Badge variant="outline" className={`text-xs ${isDark ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-emerald-300 text-emerald-800 bg-emerald-50"}`}>
                  <CheckCircle2 className="w-3 h-3 mr-1 shrink-0" />
                  Ses dosyası düzeltildi
                </Badge>
              )}
              {reportStatus === "not_found" && (
                <Badge variant="outline" className={`text-xs ${isDark ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : "border-rose-300 text-rose-800 bg-rose-50"}`}>
                  <XCircle className="w-3 h-3 mr-1 shrink-0" />
                  Ses dosyası bulunamadı
                </Badge>
              )}
              {reportStatus === "pending" && (
                <Badge variant="outline" className={`text-xs ${isDark ? "border-amber-500/40 text-amber-300 bg-amber-500/10" : "border-amber-300 text-amber-800 bg-amber-50"}`}>
                  <Info className="w-3 h-3 mr-1 shrink-0" />
                  Ses dosyası inceleniyor
                </Badge>
              )}
            </DialogTitle>
            {recordId != null && !existingReport && !transferPending && (
              loadError ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={checkingReport || reportingType !== null}
                  onClick={() => submitRecordingError("missing")}
                  className={`h-7 px-2.5 text-xs shrink-0 ${isDark ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-rose-300 text-rose-700 hover:bg-rose-50"}`}
                >
                  {checkingReport || reportingType === "missing" ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                  )}
                  Dosya Bulunamadı Bildir
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={checkingReport || reportingType !== null || isLoading}
                  onClick={() => setReportMenuOpen((open) => !open)}
                  className={`h-7 px-2.5 text-xs shrink-0 ${isDark ? "border-slate-700 text-amber-300 hover:bg-slate-800" : "border-amber-200 text-amber-700 hover:bg-amber-50"} ${reportMenuOpen ? (isDark ? "bg-slate-800" : "bg-amber-50") : ""}`}
                >
                  {checkingReport || reportingType ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                  )}
                  Hata Bildir
                </Button>
              )
            )}
          </div>
          {reportMenuOpen && !loadError && !transferPending && recordId != null && !existingReport && playableReportOptions.length > 0 && (
            <div className={`rounded-xl border p-3 space-y-2 ${isDark ? "border-slate-700 bg-slate-800/80" : "border-amber-200 bg-amber-50/80"}`}>
              <p className={`text-xs font-medium ${isDark ? "text-slate-300" : "text-amber-900"}`}>Sorun türünü seçin</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {playableReportOptions.map((type) => (
                  <Button
                    key={type}
                    type="button"
                    variant="outline"
                    disabled={reportingType !== null}
                    onClick={() => submitRecordingError(type)}
                    className={`justify-start h-auto py-2.5 px-3 text-left whitespace-normal ${isDark ? "border-slate-600 hover:bg-slate-700" : "border-amber-200 hover:bg-white bg-white"}`}
                  >
                    {reportingType === type ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2 shrink-0" />
                    )}
                    <span className="min-w-0">
                      <span className={`block text-sm font-medium ${isDark ? "text-slate-100" : "text-gray-900"}`}>
                        {RECORDING_ERROR_LABELS[type]}
                      </span>
                      <span className={`block text-xs mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                        {RECORDING_ERROR_HINTS[type]}
                      </span>
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogHeader>
        <div className="space-y-4">
          {(infoRows.length > 0 || displayInfo) && (
            <div className={`p-4 rounded-lg ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              {playerBusy && !loadError ? (
                <div className="grid grid-cols-2 gap-3 animate-pulse">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className={`h-5 rounded ${isDark ? "bg-slate-700/70" : "bg-gray-200"}`} />
                  ))}
                </div>
              ) : infoRows.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {infoRows.map((row) => (
                    <div key={row.label} className="flex items-start gap-2 min-w-0">
                      <span className={`shrink-0 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{row.label}:</span>
                      <span className={`font-medium break-all ${row.mono ? "font-mono text-xs" : ""}`}>{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <PhoneIncoming className={`w-4 h-4 ${isDark ? "text-green-400" : "text-green-600"}`} />
                    <span className={isDark ? "text-slate-400" : "text-gray-500"}>Arayan:</span>
                    <span className="font-mono font-medium">{displayInfo.arayan_numara}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PhoneOutgoing className={`w-4 h-4 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                    <span className={isDark ? "text-slate-400" : "text-gray-500"}>Aranan:</span>
                    <span className="font-mono font-medium">{displayInfo.aranan_numara}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-gray-500"}`} />
                    <span className={isDark ? "text-slate-400" : "text-gray-500"}>Tarih:</span>
                    <span className="font-mono">{displayInfo.tarih}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className={`w-4 h-4 ${isDark ? "text-slate-400" : "text-gray-500"}`} />
                    <span className={isDark ? "text-slate-400" : "text-gray-500"}>Süre:</span>
                    <span className="font-mono">{formatDuration(displayInfo.konusma_suresi)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {panelProgressLabel ? (
            <p className={`text-xs px-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>{panelProgressLabel}</p>
          ) : null}

          {transferPending && (
            <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-xs ${isDark ? "border-amber-500/30 bg-amber-500/10 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
              <Upload className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
              <div className="space-y-1">
                <p className="font-medium">Ses dosyası sisteme aktarılıyor</p>
                <p className={isDark ? "text-amber-200/80" : "text-amber-800/90"}>
                  Son 6 dakika içindeki çağrılar için kayıt birkaç dakika gecikebilir. Otomatik yeniden deneniyor
                  {pendingRemainingMs > 0 ? ` (${formatPendingRemaining(pendingRemainingMs)} kaldı)` : ""}.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={reloadAudio}
                  className={`h-7 mt-1 ${isDark ? "border-amber-500/40 text-amber-200 hover:bg-amber-500/10" : "border-amber-300 text-amber-800 hover:bg-white"}`}
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Şimdi yeniden dene
                </Button>
              </div>
            </div>
          )}

          {loadError && !transferPending && !existingReport && (
            <p className={`text-xs px-1 ${isDark ? "text-rose-300/90" : "text-rose-700"}`}>
              Ses dosyası yüklenemedi
              {recordId != null && !withinPendingWindow ? " (6 dk bekleme süresi doldu). " : ". "}
              {recordId != null ? (
                <><strong>Dosya Bulunamadı Bildir</strong> ile tek tıkla bildirebilirsiniz.</>
              ) : (
                <>Bağlantınızı kontrol edip tekrar deneyin.</>
              )}
            </p>
          )}

          {withinPendingWindow && isLoading && !loadError && !transferPending && shouldExpectRecording(recordInfo) && (
            <p className={`text-xs px-1 ${isDark ? "text-amber-200/80" : "text-amber-700"}`}>
              Kayıt henüz sisteme aktarılıyor olabilir…
            </p>
          )}

          {existingReport && (
            <div className={`rounded-lg border px-3 py-2 flex items-start gap-2 text-xs ${
              reportStatus === "fixed"
                ? (isDark ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800")
                : reportStatus === "not_found"
                  ? (isDark ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-800")
                  : (isDark ? "border-amber-500/30 bg-amber-500/10 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800")
            }`}>
              {reportStatus === "fixed" ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              ) : reportStatus === "not_found" ? (
                <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <span>
                {getReportStatusLabel(reportStatus)}
                {existingReport.error_type ? ` (${getRecordingErrorLabel(existingReport.error_type)})` : ""}.
                {reportStatus === "pending" && " Teknik ekip incelemede."}
                {reportStatus === "fixed" && existingReport.resolved_by_name ? ` İşlem: ${existingReport.resolved_by_name}.` : ""}
                {reportStatus === "not_found" && existingReport.resolved_by_name ? ` İşlem: ${existingReport.resolved_by_name}.` : ""}
                {existingReport.resolution_note ? ` Not: ${existingReport.resolution_note}` : ""}
              </span>
            </div>
          )}
          
          {/* Waveform-like Progress Bar */}
          <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div 
              className="h-16 relative cursor-pointer rounded-lg overflow-hidden"
              onClick={loadError || transferPending ? undefined : handleSeek}
              style={{
                background: isDark 
                  ? "linear-gradient(180deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.05) 100%)"
                  : "linear-gradient(180deg, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0.05) 100%)"
              }}
            >
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500/30 to-cyan-500/30"
                style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-around px-2">
                {waveformBars.map((i) => (
                  <div
                    key={i}
                    className={`w-1 rounded-full transition-all ${
                      i < (duration > 0 ? (currentTime / duration) * waveformBars.length : 0)
                        ? isDark ? "bg-blue-400" : "bg-blue-500"
                        : isDark ? "bg-slate-600" : "bg-gray-300"
                    }`}
                    style={{ 
                      height: `${20 + Math.sin(i * 0.5) * 15 + Math.sin(i * 0.3) * 20}%`,
                      opacity: i < (duration > 0 ? (currentTime / duration) * waveformBars.length : 0) ? 1 : 0.5
                    }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex justify-between mt-2 text-sm font-mono">
              <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                {playerBusy || transferPending ? (transferPending ? "Aktarılıyor..." : panelProgressLabel || "Yükleniyor...") : loadError ? "Yüklenemedi" : formatTime(currentTime)}
              </span>
              <span className={isDark ? "text-slate-400" : "text-gray-500"}>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => skip(-10)} disabled={playerBusy || loadError || transferPending} className={isDark ? "text-slate-300" : ""}>
              <SkipBack className="w-5 h-5" />
            </Button>
            <Button 
              onClick={togglePlay}
              disabled={playerBusy || loadError || transferPending}
              className={`w-14 h-14 rounded-full ${isDark ? "bg-blue-600 hover:bg-blue-500" : "bg-blue-500 hover:bg-blue-600"} text-white disabled:opacity-50`}
            >
              {playerBusy || transferPending ? <RefreshCw className="w-6 h-6 animate-spin" /> : isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => skip(10)} disabled={playerBusy || loadError || transferPending} className={isDark ? "text-slate-300" : ""}>
              <SkipForward className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {volume > 0 ? <Volume1 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => {
                  setVolume(parseFloat(e.target.value));
                  if (audioRef.current) audioRef.current.volume = parseFloat(e.target.value);
                }}
                className="w-20 h-1 bg-gray-300 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <Button variant="outline" size="sm" onClick={changePlaybackRate} className={isDark ? "border-slate-700" : ""}>
              {playbackRate}x
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={playerBusy || loadError || transferPending || downloading || !(usePanelBlob ? sourceRecordingUrl : (recordInfo?.cdr_id ?? recordInfo?.download_source ?? recordId))}
              onClick={handleDownloadRecording}
              className={`${isDark ? "border-slate-700 text-amber-300" : "text-amber-700"}`}
              title="İndir"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              İndir
            </Button>
            {onOpenTranscript && recordInfo && recordId != null && (
              <Button
                variant="outline"
                size="sm"
                disabled={playerBusy || loadError || transferPending || !effectiveAudioUrl || transcriptChecking}
                onClick={() => {
                  handleClose();
                  onOpenTranscript(recordInfo, { hasTranscript: transcriptHasContent });
                }}
                className={`${isDark ? "border-slate-700 text-cyan-400" : "text-cyan-600"} disabled:opacity-40`}
                title={loadError ? "Ses yüklenemediği için transkript kullanılamaz" : transcriptHasContent ? "Transkript detayına git" : "Transkript oluştur veya görüntüle"}
              >
                <FileText className="w-4 h-4 mr-1" />
                {transcriptHasContent ? "Transkripti Gör" : "Transkript"}
              </Button>
            )}
          </div>

          <audio 
            key={`${recordId ?? "none"}-${effectiveAudioUrl ?? ""}`}
            ref={audioRef}
            src={effectiveAudioUrl || undefined}
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onCanPlay={handleCanPlay}
            onError={handleAudioError}
            onEnded={() => setIsPlaying(false)}
            className="hidden"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CallRecordingPlayerModal;
